import React, { useEffect, useCallback } from "react";
import { notificationApi } from "../api/backend-loyalty.api";
import { useNotificationCenter } from "../contexts/NotificationContext";
import { BirthdayNotification } from "../types/loyalty.types";
import { loyaltyApi } from "../api/loyalty.api";

/**
 * Hook to check for upcoming customer birthdays
 * Automatically generates notifications on login/mount
 * Supports advance notifications (1 day before, etc.)
 * Falls back to local customer data if backend API is not available
 */
/**
 * Session-level key used to track whether the birthday check has already
 * been performed today. We use sessionStorage (cleared when the browser tab
 * closes) combined with today's date so the check re-runs the next calendar
 * day without requiring a full logout/login cycle.
 */
const BIRTHDAY_CHECK_SESSION_KEY = "birthday_check_date";

/**
 * Returns true if the birthday check has already been run today in this
 * browser session, false otherwise.
 */
const hasBirthdayCheckRunToday = (): boolean => {
  try {
    const lastCheck = sessionStorage.getItem(BIRTHDAY_CHECK_SESSION_KEY);
    if (!lastCheck) return false;
    const today = new Date().toISOString().split("T")[0]; // YYYY-MM-DD
    return lastCheck === today;
  } catch {
    return false;
  }
};

/**
 * Records that the birthday check was run today so subsequent calls within
 * the same day / session are skipped.
 */
const markBirthdayCheckRanToday = (): void => {
  try {
    const today = new Date().toISOString().split("T")[0]; // YYYY-MM-DD
    sessionStorage.setItem(BIRTHDAY_CHECK_SESSION_KEY, today);
  } catch {
    // Ignore storage errors silently
  }
};

export const useBirthdayCheck = () => {
  const { addBulkNotifications, unreadCount } = useNotificationCenter();

  const checkBirthdays = useCallback(async () => {
    try {
      // Guard: skip if we already ran the birthday check today in this session
      if (hasBirthdayCheckRunToday()) {
        return;
      }

      const restaurantId = localStorage.getItem("businessId");
      // Fallback: check both storage keys used across different login flows
      const token =
        localStorage.getItem("authToken") || localStorage.getItem("token");

      if (!restaurantId || !token) {
        return;
      }

      // Validate JWT expiry locally BEFORE making any API calls.
      // This prevents authenticated API requests from firing on public pages
      // when the stored token is stale/expired, which would trigger 401 errors
      // and wrongly redirect the user from public pages to /login.
      try {
        const tokenPayload = JSON.parse(atob(token.split(".")[1]));
        const currentTime = Math.floor(Date.now() / 1000);
        if (tokenPayload.exp && tokenPayload.exp < currentTime) {
          return;
        }
      } catch {
        return;
      }


      // Mark check as run BEFORE making any API calls so that even if the
      // calls fail we don't spam the backend on every subsequent navigation.
      markBirthdayCheckRanToday();

      // Track which customers we've already created notifications for (deduplication)
      const processedCustomerIds = new Set<string>();

      // Try to check for upcoming birthdays from backend
      let result: any = null;
      let backendError = false;

      try {
        result = await notificationApi.checkUpcomingBirthdays(
          parseInt(restaurantId),
          token,
        );
      } catch (apiError) {
        console.error("❌ Backend API call failed:", apiError);
        backendError = true;
      }

      // Check if backend returned valid data
      const hasBackendData =
        !backendError &&
        result &&
        ((result?.todayBirthdays && result.todayBirthdays.length > 0) ||
          (result?.upcomingBirthdays && result.upcomingBirthdays.length > 0));

      if (!hasBackendData) {

        // Fallback: Fetch customers locally and check birthdays
        try {
          const customers = await loyaltyApi.getCustomers(1, 1000); // Fetch all customers


          if (customers.length > 0) {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const notifications: BirthdayNotification[] = [];
            const processedIds = new Set<string>();


            customers.forEach((customer: any, index: number) => {
              // Skip if we've already created notification for this customer
              if (processedIds.has(customer.id)) {
                return;
              }

              // Get birthday from customer data
              const birthDate =
                customer.demographics?.birthdate || customer.dateOfBirth;

              if (!birthDate) {
                return;
              }


              // Parse birthday (format: YYYY-MM-DD or DD-MM-YYYY)
              let birthMonth: number, birthDay: number;

              const parts = birthDate.split("-");
              if (parts.length === 3) {
                // Check if it's YYYY-MM-DD or DD-MM-YYYY
                if (parts[0].length === 4) {
                  // YYYY-MM-DD
                  birthMonth = parseInt(parts[1]);
                  birthDay = parseInt(parts[2]);
                } else {
                  // DD-MM-YYYY
                  birthDay = parseInt(parts[0]);
                  birthMonth = parseInt(parts[1]);
                }
              } else {
                return; // Invalid format
              }


              // Create birthday date for this year
              const thisYearBirthday = new Date(
                today.getFullYear(),
                birthMonth - 1,
                birthDay,
              );
              thisYearBirthday.setHours(0, 0, 0, 0);

              // If birthday already passed this year, check next year
              let nextBirthday = thisYearBirthday;
              if (nextBirthday < today) {
                nextBirthday = new Date(
                  today.getFullYear() + 1,
                  birthMonth - 1,
                  birthDay,
                );
                nextBirthday.setHours(0, 0, 0, 0);
              }

              // Calculate days until birthday
              const daysUntil = Math.ceil(
                (nextBirthday.getTime() - today.getTime()) /
                  (1000 * 60 * 60 * 24),
              );


              // Check if birthday is within next 30 days
              if (daysUntil >= 0 && daysUntil <= 30) {
                let notificationType: "today" | "tomorrow" | "upcoming";
                let message: string;

                if (daysUntil === 0) {
                  notificationType = "today";
                  message = `🎂 Happy Birthday! ${
                    customer.name || "Customer"
                  }'s birthday is today!`;
                } else if (daysUntil === 1) {
                  notificationType = "tomorrow";
                  message = `📅 ${
                    customer.name || "Customer"
                  }'s birthday is tomorrow!`;
                } else {
                  notificationType = "upcoming";
                  message = `🎁 ${
                    customer.name || "Customer"
                  }'s birthday is in ${daysUntil} days.`;
                }


                notifications.push({
                  id: `birthday_${notificationType}_${customer.id}`,
                  customerId: customer.id,
                  customerName: customer.name || "Customer",
                  customerPhone: customer.phone || "",
                  customerEmail: customer.email || "",
                  birthdayDate: birthDate,
                  notificationType,
                  message,
                  isRead: false,
                  createdAt: new Date().toISOString(),
                  reminderDaysAdvance: daysUntil,
                });

                // Mark as processed
                processedIds.add(customer.id);
              }
            });

            if (notifications.length > 0) {
              addBulkNotifications(notifications);
            } else {
            }
          }
        } catch (localError) {
          console.error("❌ Failed to check birthdays locally:", localError);
        }

        return; // Exit early since we used local fallback
      }

      // Backend returned data, process it

      // Convert backend response to local notification format
      const allNotifications: BirthdayNotification[] = [];

      if (result?.todayBirthdays && Array.isArray(result.todayBirthdays)) {
        const notifications: BirthdayNotification[] = result.todayBirthdays
          .filter((customer: any) => {
            const customerId = customer.customerId || customer.id;
            // Skip if already processed
            if (processedCustomerIds.has(customerId)) {
              return false;
            }
            processedCustomerIds.add(customerId);
            return true;
          })
          .map((customer: any, index: number) => ({
            id: `birthday_today_${customer.customerId || customer.id}`,
            customerId: customer.customerId || customer.id,
            customerName: customer.fullName || customer.name || "Customer",
            customerPhone: customer.phoneNumber || customer.phone || "",
            customerEmail: customer.email,
            birthdayDate: customer.dateOfBirth || "",
            notificationType: "today" as const,
            message: `Happy Birthday! ${
              customer.fullName || "Customer"
            }'s birthday is today.`,
            isRead: false,
            createdAt: new Date().toISOString(),
            reminderDaysAdvance: 0,
          }));

        allNotifications.push(...notifications);

        if (notifications.length > 0) {
        }
      }

      // Check for upcoming birthdays (next 30 days)
      if (
        result?.upcomingBirthdays &&
        Array.isArray(result.upcomingBirthdays)
      ) {
        const upcomingNotifications: BirthdayNotification[] =
          result.upcomingBirthdays
            .filter((customer: any) => {
              const customerId = customer.customerId || customer.id;
              // Skip if already processed
              if (processedCustomerIds.has(customerId)) {
                return false;
              }
              processedCustomerIds.add(customerId);
              return true;
            })
            .map((customer: any, index: number) => {
              // Calculate days in advance
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const birthdayStr = customer.dateOfBirth || "";
              const [year, month, day] = birthdayStr.split("-");
              const birthDate = new Date(
                today.getFullYear(),
                parseInt(month) - 1,
                parseInt(day),
              );
              birthDate.setHours(0, 0, 0, 0);

              if (birthDate < today) {
                birthDate.setFullYear(today.getFullYear() + 1);
              }

              const daysAdvance = Math.ceil(
                (birthDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
              );

              return {
                id: `birthday_upcoming_${customer.customerId || customer.id}`,
                customerId: customer.customerId || customer.id,
                customerName: customer.fullName || customer.name || "Customer",
                customerPhone: customer.phoneNumber || customer.phone || "",
                customerEmail: customer.email,
                birthdayDate: customer.dateOfBirth || "",
                notificationType:
                  daysAdvance === 1
                    ? ("tomorrow" as const)
                    : ("upcoming" as const),
                message:
                  daysAdvance === 1
                    ? `${
                        customer.fullName || "Customer"
                      }'s birthday is tomorrow!`
                    : `${
                        customer.fullName || "Customer"
                      }'s birthday is coming up in ${daysAdvance} days.`,
                isRead: false,
                createdAt: new Date().toISOString(),
                reminderDaysAdvance: daysAdvance,
              };
            });

        allNotifications.push(...upcomingNotifications);

        if (upcomingNotifications.length > 0) {
        }
      }

      if (allNotifications.length > 0) {
        addBulkNotifications(allNotifications);
      }
    } catch (error) {
      console.error("❌ Error checking birthdays:", error);
    }
  }, [addBulkNotifications]);

  // Run birthday check on mount (when component loads)
  useEffect(() => {
    checkBirthdays();

    // Listen to local customer registration events to recheck birthdays
    const handleCustomerAdded = () => {
      checkBirthdays();
    };

    window.addEventListener("loyalty:customer-added", handleCustomerAdded);

    return () => {
      window.removeEventListener("loyalty:customer-added", handleCustomerAdded);
    };
  }, [checkBirthdays]);

  return {
    checkBirthdays,
    unreadCount,
  };
};

/**
 * Advanced birthday check with local customer list fallback
 * Uses local customer data if backend doesn't have birthday endpoint
 */
export const useBirthdayCheckLocal = (customers: any[] = []) => {
  const { addBulkNotifications, unreadCount } = useNotificationCenter();

  const checkBirthdaysLocally = useCallback(() => {
    if (!customers || customers.length === 0) {
      return;
    }

    const today = new Date();
    const todayMonth = String(today.getMonth() + 1).padStart(2, "0");
    const todayDay = String(today.getDate()).padStart(2, "0");
    const todayMMDD = `${todayMonth}-${todayDay}`;

    const notifications: BirthdayNotification[] = [];

    customers.forEach((customer: any, index: number) => {
      const birthDate =
        customer.demographics?.birthdate || customer.dateOfBirth;

      if (!birthDate) return;

      // Extract MM-DD from date
      const dateParts = birthDate.split("-");
      const birthMMDD =
        dateParts.length >= 3 ? `${dateParts[1]}-${dateParts[2]}` : birthDate;

      // Check if birthday is today
      if (birthMMDD === todayMMDD) {
        notifications.push({
          id: `birthday_today_${customer.id}_${Date.now()}`,
          customerId: customer.id,
          customerName: customer.name || "Customer",
          customerPhone: customer.phone || "",
          customerEmail: customer.email,
          birthdayDate: birthDate,
          notificationType: "today",
          message: `🎂 ${customer.name || "Customer"}'s birthday is today!`,
          isRead: false,
          createdAt: new Date().toISOString(),
          reminderDaysAdvance: 0,
        });
      }

      // Check if birthday is tomorrow
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowMonth = String(tomorrow.getMonth() + 1).padStart(2, "0");
      const tomorrowDay = String(tomorrow.getDate()).padStart(2, "0");
      const tomorrowMMDD = `${tomorrowMonth}-${tomorrowDay}`;

      if (birthMMDD === tomorrowMMDD) {
        notifications.push({
          id: `birthday_tomorrow_${customer.id}_${Date.now()}`,
          customerId: customer.id,
          customerName: customer.name || "Customer",
          customerPhone: customer.phone || "",
          customerEmail: customer.email,
          birthdayDate: birthDate,
          notificationType: "tomorrow",
          message: `📅 ${customer.name || "Customer"}'s birthday is tomorrow!`,
          isRead: false,
          createdAt: new Date().toISOString(),
          reminderDaysAdvance: 1,
        });
      }

      // Check upcoming birthdays (next 30 days)
      for (let i = 2; i <= 30; i++) {
        const checkDate = new Date(today);
        checkDate.setDate(checkDate.getDate() + i);
        const checkMonth = String(checkDate.getMonth() + 1).padStart(2, "0");
        const checkDay = String(checkDate.getDate()).padStart(2, "0");
        const checkMMDD = `${checkMonth}-${checkDay}`;

        if (birthMMDD === checkMMDD) {
          notifications.push({
            id: `birthday_upcoming_${customer.id}_${i}_${Date.now()}`,
            customerId: customer.id,
            customerName: customer.name || "Customer",
            customerPhone: customer.phone || "",
            customerEmail: customer.email,
            birthdayDate: birthDate,
            notificationType: "upcoming",
            message: `📅 ${
              customer.name || "Customer"
            }'s birthday is in ${i} days.`,
            isRead: false,
            createdAt: new Date().toISOString(),
            reminderDaysAdvance: i,
          });
          break; // Only one upcoming notification per customer
        }
      }
    });

    if (notifications.length > 0) {
      addBulkNotifications(notifications);

      if (process.env.NODE_ENV === "development") {
      }
    }
  }, [customers, addBulkNotifications]);

  return {
    checkBirthdaysLocally,
    unreadCount,
  };
};
